import { useMutation } from '@apollo/client';
import type { WorkflowConfiguration, WorkflowType } from '@communities/content-management/domain';
import { UPSERT_COMMUNITY_GROUP_WORKFLOWS, GET_COMMUNITY_GROUP_WORKFLOWS } from '@communities/content-management/graphql';
import { transformErrors } from '@core/adapters/errors/transformError';
import type { DomainError } from '@core/domain';
import { track } from '@soteria-ui/src/core/logging';
import { useCallback, useState } from 'react';

interface UpsertCommunityGroupWorkflowResponse {
  upsertCommunityGroupWorkflowConfiguration: WorkflowConfiguration;
}

export function useUpsertCommunityGroupWorkflow(communityGroupId?: string) {
  const [errors, setErrors] = useState<DomainError[] | null>(null);

  const [updateWorkflowMutation, { loading }] = useMutation<UpsertCommunityGroupWorkflowResponse>(UPSERT_COMMUNITY_GROUP_WORKFLOWS, {
    refetchQueries: communityGroupId ? [{ query: GET_COMMUNITY_GROUP_WORKFLOWS, variables: { communityGroupId } }] : [],
    awaitRefetchQueries: true,
  });

  const resetErrors = useCallback(() => {
    setErrors(null);
  }, []);

  const upsertWorkflow = async (communityGroupId: string, type: WorkflowType, updatedWorkflow: WorkflowConfiguration) => {
    try {
      track('UpsertCommunityGroupWorkflowInitiated', {
        communityGroupId,
        workflowType: type,
      });

      const completeWorkflow = {
        topicActions: updatedWorkflow.topicActions || [],
      };

      await updateWorkflowMutation({
        variables: {
          communityGroupId,
          type,
          input: completeWorkflow,
        },
      });

      track('UpsertCommunityGroupWorkflowSucceeded', {
        communityGroupId,
        workflowType: type,
      });

      return true;
    } catch (error) {
      track('UpsertCommunityGroupWorkflowFailed', {
        communityGroupId,
        workflowType: type,
        errorMessage: error instanceof Error ? error.message : 'Unknown error',
      });

      setErrors(transformErrors(error));

      return false;
    }
  };

  return {
    upsertWorkflow,
    loading,
    errors,
    resetErrors,
  };
}

export default useUpsertCommunityGroupWorkflow;
